import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CarOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../services/axiosInstance";
import {
  CREATE_RAKE_DRIVER,
  TRACKING_RAKE_DRIVERS,
} from "../../services/apiEndPoints";
import { IRakeDriverWithLatest } from "../types/tracking.types";
import DriverTrailDrawer from "./DriverTrailDrawer";
import { toastSuccess, toastError } from "../../utils/toast";

dayjs.extend(relativeTime);

const { Text } = Typography;

interface RakeDriversListProps {
  rakeMasterId: string;
  autoRefreshMs?: number;
}

const GRANTED_CONSENT_STATUSES = new Set([
  "approved",
  "consent_approved",
  "granted",
  "given",
  "allowed",
  "accepted",
  "active",
  "yes",
  "true",
]);

const isConsentGranted = (status?: string | null) => {
  if (!status) return false;
  return GRANTED_CONSENT_STATUSES.has(String(status).toLowerCase());
};

const consentLabel = (status?: string | null) => {
  if (!status) return { color: "default" as const, text: "Consent unknown" };
  const lower = String(status).toLowerCase();
  if (GRANTED_CONSENT_STATUSES.has(lower)) {
    return { color: "success" as const, text: `Consent ${status}` };
  }
  if (lower === "pending") {
    return { color: "warning" as const, text: "Consent pending" };
  }
  return { color: "error" as const, text: `Consent ${status}` };
};

const RakeDriversList = ({
  rakeMasterId,
  autoRefreshMs,
}: RakeDriversListProps) => {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState<IRakeDriverWithLatest[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<IRakeDriverWithLatest | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchDrivers = useCallback(async () => {
    if (!rakeMasterId) return;
    setLoading(true);
    try {
      const res = await axios.get(TRACKING_RAKE_DRIVERS(rakeMasterId));
      const results: IRakeDriverWithLatest[] = res?.data?.results ?? [];
      setDrivers(results);
    } catch {
      toastError("Failed to load rake drivers");
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const interval = window.setInterval(fetchDrivers, autoRefreshMs);
    return () => window.clearInterval(interval);
  }, [autoRefreshMs, fetchDrivers]);

  const openDrawer = (driver: IRakeDriverWithLatest) => {
    setSelected(driver);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const handleAddDriver = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setAddLoading(true);
      await axios.post(CREATE_RAKE_DRIVER, {
        name: values.name.trim(),
        phoneNumber: values.phoneNumber.trim(),
        ...(values.truckNumber ? { truckNumber: values.truckNumber.trim() } : {}),
        activeRakeId: rakeMasterId,
      });
      toastSuccess("Driver added to rake");
      form.resetFields();
      setAddModalOpen(false);
      await fetchDrivers();
    } catch (error: any) {
      const msg = error?.response?.data?.error;
      if (msg) toastError(msg);
    } finally {
      setAddLoading(false);
    }
  }, [form, rakeMasterId, fetchDrivers]);

  return (
    <>
      <Card
        className="content-card"
        style={{ borderRadius: 14, marginBottom: 20 }}
        styles={{ body: { padding: "20px 24px" } }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div>
            <Text strong style={{ fontSize: 16 }}>
              <TeamOutlined style={{ marginRight: 6, color: "#1677ff" }} />
              Rake drivers
            </Text>
            <Text
              style={{
                color: "#94a3b8",
                fontSize: 12,
                display: "block",
                marginTop: 2,
              }}
            >
              {drivers.length} driver{drivers.length === 1 ? "" : "s"} assigned to this rake
            </Text>
          </div>
          <Space>
            <Tooltip title="Refresh">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchDrivers}
                loading={loading}
              />
            </Tooltip>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddModalOpen(true)}
            >
              Add driver
            </Button>
          </Space>
        </div>

        {loading && drivers.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Loading drivers...</Text>
            </div>
          </div>
        ) : drivers.length === 0 ? (
          <Empty description="No drivers assigned to this rake" />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 12,
            }}
          >
            {drivers.map((d) => {
              const consent = consentLabel(d.consentStatus);
              const granted = isConsentGranted(d.consentStatus);
              return (
                <div
                  key={d.rakeDriverId}
                  onClick={() => openDrawer(d)}
                  style={{
                    cursor: "pointer",
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    background: "#fff",
                    padding: 14,
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#1677ff";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(22,119,255,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Space size={10} align="start" style={{ width: "100%" }}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, #1677ff, #4096ff)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {d.name?.[0]?.toUpperCase() || "D"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        strong
                        style={{
                          fontSize: 14,
                          display: "block",
                          color: "#0f172a",
                        }}
                        ellipsis
                      >
                        {d.name}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          display: "block",
                        }}
                      >
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        {d.phoneNumber}
                      </Text>
                      {d.truckNumber && (
                        <Text
                          style={{
                            fontSize: 12,
                            color: "#64748b",
                            display: "block",
                          }}
                        >
                          <CarOutlined style={{ marginRight: 4 }} />
                          {d.truckNumber}
                        </Text>
                      )}
                    </div>
                  </Space>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      marginTop: 10,
                    }}
                  >
                    <Tag color={consent.color} style={{ margin: 0 }}>
                      {consent.text}
                    </Tag>
                    {d.isTracked ? (
                      <Tag
                        icon={<SyncOutlined spin />}
                        color="processing"
                        style={{ margin: 0 }}
                      >
                        Tracking
                      </Tag>
                    ) : (
                      <Tag color="default" style={{ margin: 0 }}>
                        Not tracked
                      </Tag>
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      paddingTop: 10,
                      borderTop: "1px dashed #e2e8f0",
                      fontSize: 12,
                      color: "#475569",
                      minHeight: 36,
                    }}
                  >
                    {!granted ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Consent not given — location unavailable
                      </Text>
                    ) : d.latest ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 6,
                          }}
                        >
                          <EnvironmentOutlined
                            style={{ color: "#10b981", marginTop: 2 }}
                          />
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {d.latest.detailedAddress ||
                              `${d.latest.latitude.toFixed(5)}, ${d.latest.longitude.toFixed(5)}`}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#94a3b8",
                            marginTop: 4,
                          }}
                        >
                          {dayjs(d.latest.timestamp).fromNow()}
                          {d.lastSyncedAt &&
                            d.lastSyncedAt !== d.latest.timestamp && (
                              <>
                                {" · synced "}
                                {dayjs(d.lastSyncedAt).fromNow()}
                              </>
                            )}
                        </div>
                      </>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        No location data yet
                      </Text>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <DriverTrailDrawer
        open={drawerOpen}
        rakeMasterId={rakeMasterId}
        driver={selected}
        onClose={closeDrawer}
      />

      <Modal
        title={
          <Space>
            <UserAddOutlined style={{ color: "#1677ff" }} />
            Add driver to rake
          </Space>
        }
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        onOk={handleAddDriver}
        confirmLoading={addLoading}
        okText="Add driver"
        width={480}
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 16 }}>
          <Form.Item
            label="Driver name"
            name="name"
            rules={[{ required: true, message: "Driver name is required" }]}
          >
            <Input prefix={<UserAddOutlined />} placeholder="Full name" />
          </Form.Item>
          <Form.Item
            label="Phone number"
            name="phoneNumber"
            rules={[
              { required: true, message: "Phone number is required" },
              {
                validator: (_, value) => {
                  if (!value || /^\d{10}$/.test(value.trim())) return Promise.resolve();
                  return Promise.reject("Enter a valid 10-digit phone number");
                },
              },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="10-digit mobile number" maxLength={10} />
          </Form.Item>
          <Form.Item label="Truck number" name="truckNumber">
            <Input prefix={<CarOutlined />} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RakeDriversList;
